import { useCallback, useLayoutEffect, useState } from 'react';
import { calculateMediaBounds } from '../ImageElementComponent/utils';
import { calculateLottieBounds } from '../LottieElementComponent/utils';
import { ElementPositions } from '@/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { LottieElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/LottieElement';

export const useMediaBounds = (elementRef: React.RefObject<HTMLDivElement>, mediaSelector: string = 'img, video') => {
  const getMediaBounds = useCallback(() => {
    const containerElement = elementRef.current;
    if (!containerElement) {
      return null;
    }

    const mediaElement = containerElement.querySelector(mediaSelector) as HTMLImageElement | HTMLVideoElement;
    if (!mediaElement) {
      return null;
    }

    return calculateMediaBounds(containerElement, mediaElement);
  }, [elementRef, mediaSelector]);

  return getMediaBounds;
};

export const useElementPositions = (
  containerStyle: ElementPositions,
  isContainMode: boolean,
  getMediaBounds: () => {
    left: number;
    top: number;
    width: number;
    height: number;
  } | null
) => {
  const [positions, setPositions] = useState<ElementPositions>({
    top: containerStyle.top,
    left: containerStyle.left,
    width: containerStyle.width,
    height: containerStyle.height
  });

  const updatePositions = useCallback((newPositions: ElementPositions) => {
    setPositions(prev => ({ ...prev, ...newPositions }));
  }, []);

  // Use useLayoutEffect to ensure DOM is updated before calculating bounds
  useLayoutEffect(() => {
    setPositions({
      top: containerStyle.top,
      left: containerStyle.left,
      width: containerStyle.width,
      height: containerStyle.height
    });
  }, [containerStyle.top, containerStyle.left, containerStyle.width, containerStyle.height]);

  // Handle contain mode positioning with proper DOM update timing
  useLayoutEffect(() => {
    if (!isContainMode) {
      setPositions({
        top: containerStyle.top,
        left: containerStyle.left,
        width: containerStyle.width,
        height: containerStyle.height
      });
      return;
    }

    if (containerStyle.width && containerStyle.height) {
      // Small delay to ensure CSS transitions/animations complete
      requestAnimationFrame(() => {
        const mediaBounds = getMediaBounds();
        if (mediaBounds) {
          setPositions({
            top: `${mediaBounds.top}%`,
            left: `${mediaBounds.left}%`,
            width: `${mediaBounds.width}%`,
            height: `${mediaBounds.height}%`
          });
        }
      });
    }
  }, [
    containerStyle.top,
    containerStyle.left,
    containerStyle.width,
    containerStyle.height,
    isContainMode,
    getMediaBounds
  ]);

  return { positions, updatePositions, setPositions };
};

export const useLottieMediaBounds = (
  elementRef: React.RefObject<HTMLDivElement>,
  getAnimationData: (element: any) => any,
  element: LottieElement
) => {
  const getLottieBounds = useCallback(() => {
    const containerElement = elementRef.current;
    if (!containerElement) {
      return null;
    }

    const animationData = getAnimationData(element);
    if (!animationData) {
      return null;
    }

    return calculateLottieBounds(containerElement, animationData);
  }, [elementRef, getAnimationData, element]);

  return getLottieBounds;
};
